import type { ExpressionConstant, ExpressionNode, Instrument, Indicator } from '../../state/types';
import {
  getEmittedFields,
  getDefaultFieldForIndicator,
  type NameGeneratorOptions,
} from '../../utils/indicatorDisplayRegistry';

export function getOperandType(
  node: ExpressionNode,
  constants: ExpressionConstant[] = []
): string {
  if (node.type === 'literal') return 'number';
  if (node.type === 'ohlc_series') return 'number';
  if (node.type === 'indicator_series') return node.field === 'date' ? 'string' : 'number';
  if (node.type === 'constant') {
    const c = constants.find((x) => x.id === node.constantId);
    return c?.type === 'dayOfWeek' ? 'string' : 'number';
  }
  return 'unknown';
}

/**
 * Resolves the "series label" for an indicator: instrument symbol, or dependent indicator's display name (recursive).
 */
export function getSeriesLabelForIndicator(
  indicator: Indicator,
  instruments: Instrument[],
  indicators: Indicator[]
): string {
  const instrument = instruments.find((inst) => inst.id === indicator.instrumentId);
  if (instrument) return instrument.symbol;
  const dependentIndicator = indicators.find((ind) => ind.id === indicator.instrumentId);
  if (dependentIndicator) {
    return dependentIndicator.name;
  }
  return 'Unknown';
}

export function getRollingSeriesOptions(
  instrumentId: string,
  instruments: Instrument[],
  indicators: Indicator[]
): NameGeneratorOptions | undefined {
  const instrument = instruments.find((inst) => inst.id === instrumentId);
  if (instrument) {
    return { defaultFieldForSeries: 'close', emittedFieldsCountForSeries: 5 };
  }
  const depInd = indicators.find((ind) => ind.id === instrumentId);
  if (depInd) {
    const emitted = getEmittedFields(depInd.type);
    const count = Object.keys(emitted).length;
    const defaultField = getDefaultFieldForIndicator(depInd.type) ?? 'Value';
    return { defaultFieldForSeries: defaultField, emittedFieldsCountForSeries: count };
  }
  return undefined;
}

export function buildAvailableSeries(
  instruments: Instrument[],
  indicators: Indicator[]
): Array<{ id: string; label: string }> {
  const primaryAndSecondarySeries = instruments.map((inst) => ({
    id: inst.id,
    label: inst.symbol,
  }));

  const indicatorSeries = indicators
    .filter((ind) => ind.type !== 'ADDITIONALINSTRUMENT')
    .map((ind) => ({
      id: ind.id,
      label: ind.name,
    }));

  return [...primaryAndSecondarySeries, ...indicatorSeries];
}
